"use client";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { auth, clearStoredAccessToken, storeAccessToken } from "./api";
import { User } from "@/types";
import { clearRecentSearch } from "./recent-search-storage";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  refreshUser: () => Promise<User | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

function isAuthCheckFailure(err: unknown) {
  const e = err as { response?: { status?: number }; code?: string; name?: string };
  if (e?.response?.status === 401 || e?.response?.status === 403) return true;
  return false;
}

function sessionPersistenceError(cause: unknown, storageWorked: boolean) {
  const sessionError = new Error(
    storageWorked
      ? "Authenticated session could not be verified after sign-in"
      : "Authenticated session was not retained after sign-in",
  ) as Error & { code?: string; cause?: unknown };
  sessionError.code = "SESSION_NOT_RETAINED";
  sessionError.cause = cause;
  return sessionError;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = async () => {
    try {
      const r = await auth.me();
      setUser(r.data);
      return r.data;
    } catch {
      setUser(null);
      return null;
    }
  };

  useEffect(() => {
    refreshUser()
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    const loginResponse = await auth.login(email.trim().toLowerCase(), password);
    const storageWorked = storeAccessToken(loginResponse.data.access_token);
    let me;
    try {
      me = await auth.me();
    } catch (err) {
      if (isAuthCheckFailure(err)) throw sessionPersistenceError(err, storageWorked);
      throw err;
    }
    setUser(me.data);
  };

  const signup = async (name: string, email: string, password: string) => {
    const signupResponse = await auth.signup(name, email.trim().toLowerCase(), password);
    const storageWorked = storeAccessToken(signupResponse.data.access_token);
    let me;
    try {
      me = await auth.me();
    } catch (err) {
      if (isAuthCheckFailure(err)) throw sessionPersistenceError(err, storageWorked);
      throw err;
    }
    setUser(me.data);
  };

  const logout = async () => {
    const currentUser = user;
    try {
      await auth.logout();
    } catch {}
    clearStoredAccessToken();
    clearRecentSearch(currentUser);
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, signup, refreshUser, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
